import React, { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { normalizeInstitutionType } from '../config/institutionTypes';
import { resolveLabels } from '../config/labels';
import ProfileAvatar from './ProfileAvatar';
import useEntitlements from '../hooks/useEntitlements';

export default function Sidebar({ navigation, superAdmin, open, onClose }) {
  const { profile, logout, can } = useAuth();
  const { settings } = useSettings();
  const {moduleAllowed: entitlementModuleAllowed,submoduleAllowed,featureAllowed: entitlementFeatureAllowed,typeAllowed: entitlementTypeAllowed,statusFor} = useEntitlements();
  const location = useLocation();
  const [manualOpen, setManualOpen] = useState({});
  const labels = resolveLabels(settings);
  const relabel = (text='') => {
    const map = [
      ['Teacher / Faculty', labels.teacher], ['Teachers & Staff', `${labels.teachers} & Staff`], ['Teacher', labels.teacher],
      ['Courses / Classes', labels.courses], ['Course', labels.course], ['Batches / Sections', labels.sections], ['Batch', labels.section],
      ['Parents / Guardians', `${labels.parent}s`], ['Parent', labels.parent], ['Students', labels.students], ['Student', labels.student],
      ['Branches', labels.branches], ['Branch', labels.branch], ['Academic Years', labels.session]
    ];
    return map.reduce((v,[a,b])=>String(v).split(a).join(b||a),String(text));
  };
  const visible = useMemo(() => {
    const type = normalizeInstitutionType(settings.institutionType || settings.type);
    const moduleAllowedKey = (key) => superAdmin || !key || ['dashboard','settings'].includes(key) || entitlementModuleAllowed(key);
    const typeAllowed = (item) => superAdmin || entitlementTypeAllowed(item.types);
    const featureAllowed = (item) => superAdmin || entitlementFeatureAllowed(item.featureKey);
    const permissionAllowed = (item) => superAdmin || !item.permission || can(item.permission);
    return navigation.map((item) => {
      if (!typeAllowed(item) || !featureAllowed(item)) return null;
      const children=(item.children||[]).filter(child=>{
        const childModule=child.moduleKey||item.moduleKey;
        return typeAllowed(child)&&featureAllowed(child)&&permissionAllowed(child)&&moduleAllowedKey(childModule)&&submoduleAllowed(childModule,child.submoduleKey||child.label||child.path);
      }).map(child=>{const childModule=child.moduleKey||item.moduleKey;return {...child,label:relabel(child.label),isAddon:!superAdmin&&statusFor(childModule)==='active'};});
      const ownAllowed=permissionAllowed(item)&&moduleAllowedKey(item.moduleKey);
      if(item.children?.length){
        if(!children.length)return item.path&&ownAllowed?{...item,label:relabel(item.label),children,isAddon:!superAdmin&&statusFor(item.moduleKey)==='active'}:null;
        // A grouping parent can remain visible when a separately purchased child module is allowed.
        return {...item,label:relabel(item.label),children,isAddon:!superAdmin&&statusFor(item.moduleKey)==='active'};
      }
      if(!ownAllowed)return null;
      return {...item,label:relabel(item.label),children,isAddon:!superAdmin&&statusFor(item.moduleKey)==='active'};
    }).filter(Boolean);
  }, [navigation, superAdmin, can, settings.institutionType, settings.type, settings.labels, entitlementModuleAllowed, submoduleAllowed, entitlementFeatureAllowed, entitlementTypeAllowed, statusFor]);
  const displayName = superAdmin ? (settings.platformName || 'CoachSphere') : (settings.displayName || 'Institute / School');
  const initials = displayName.split(' ').slice(0, 2).map((part) => part[0]).join('').toUpperCase();

  return (
    <>
      <aside className={`sidebar ${open ? 'sidebar-open' : ''}`}>
        <div className="brand-card">
          {settings.logoUrl ? <img src={settings.logoUrl} alt="Logo" /> : <div className="brand-initials">{initials}</div>}
          <div><strong>{displayName}</strong><span>{superAdmin ? 'SUPER ADMIN' : 'INSTITUTE WORKSPACE'}</span></div>
        </div>
        <div className="tenant-pills"><span>• {superAdmin ? 'PLATFORM WORKSPACE' : 'INSTITUTE WORKSPACE'}</span><span>🔐 {profile?.roleKey || 'superadmin'}</span></div>
        <p className="sidebar-label">ROLE-ENABLED MODULES</p>
        <nav className="sidebar-nav">
          {visible.map((item) => {
            const children = (item.children || []).filter((child) => superAdmin || !child.permission || can(child.permission));
            const activeChild = children.some((child) => location.pathname.startsWith(child.path));
            const expanded = manualOpen[item.label] ?? activeChild;
            if (children.length) {
              return (
                <div className={`nav-group ${activeChild ? 'active-group' : ''}`} key={item.label}>
                  <button className="nav-main" onClick={() => setManualOpen((prev) => ({ ...prev, [item.label]: !expanded }))}>
                    <span className="nav-icon">{item.icon}</span><span className="nav-label-wrap"><span>{item.label}</span>{item.isAddon&&<em className="nav-addon-badge">ADD-ON</em>}</span><b>{expanded ? '⌃' : '⌄'}</b>
                  </button>
                  {expanded && <div className="nav-children">{children.map((child) => <NavLink onClick={onClose} key={child.path} to={child.path} className={({ isActive }) => isActive ? 'active' : ''}><span>{child.label}</span>{child.isAddon&&<em className="nav-addon-badge mini">ADD-ON</em>}</NavLink>)}</div>}
                </div>
              );
            }
            return <NavLink onClick={onClose} key={item.path} to={item.path} className={({ isActive }) => `nav-main ${isActive ? 'active' : ''}`}><span className="nav-icon">{item.icon}</span><span className="nav-label-wrap"><span>{item.label}</span>{item.isAddon&&<em className="nav-addon-badge">ADD-ON</em>}</span></NavLink>;
          })}
        </nav>
        <div className="sidebar-user">
          <ProfileAvatar src={profile?.photoUrl} name={profile?.name || profile?.email || 'User'} size="sm" />
          <div><strong>{profile?.name || 'Portal User'}</strong><span>{profile?.roleKey || (superAdmin ? 'superadmin' : 'user')}</span></div>
          <button onClick={logout} title="Logout">🚪</button>
        </div>
      </aside>
      {open && <button className="sidebar-scrim" onClick={onClose} aria-label="Close navigation" />}
    </>
  );
}
